// ForgotPassword.tsx - Forgot password form component
import { useState } from 'react';
import {
  Paper,
  Stack,
  TextInput,
  Button,
  Text,
  Title,
  Alert,
  Group,
  Center,
} from '@mantine/core';
import { IconMail, IconCheck, IconAlertCircle, IconArrowLeft } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';

export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL || 'https://carleads-api-staging.carleads.workers.dev'}/api/auth/forgot-password`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email }),
        }
      );

      const data = await response.json();

      if (data.success) {
        setSuccess(true);
      } else {
        setError(data.error || 'Failed to send reset email');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Center style={{ minHeight: '100vh' }}>
        <Paper p="xl" radius="md" withBorder style={{ maxWidth: 400, width: '100%' }}>
          <Stack align="center" gap="md">
            <IconCheck size={64} color="green" />
            <Title order={2} ta="center">
              Check Your Email
            </Title>
            <Text ta="center" c="dimmed">
              If an account exists with <strong>{email}</strong>, we've sent you a password reset link.
            </Text>
            <Text size="sm" ta="center" c="dimmed">
              Please check your inbox and click the link to reset your password.
            </Text>
            <Button
              fullWidth
              variant="subtle"
              leftSection={<IconArrowLeft size={16} />}
              onClick={() => navigate('/login')}
            >
              Back to Login
            </Button>
          </Stack>
        </Paper>
      </Center>
    );
  }

  return (
    <Center style={{ minHeight: '100vh' }}>
      <Paper p="xl" radius="md" withBorder style={{ maxWidth: 400, width: '100%' }}>
        <Stack gap="md">
          <div>
            <Title order={2} mb="xs">
              Forgot Password?
            </Title>
            <Text c="dimmed" size="sm">
              Enter your email address and we'll send you a link to reset your password.
            </Text>
          </div>

          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" title="Error">
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Stack gap="md">
              <TextInput
                label="Email Address"
                placeholder="your@email.com"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                leftSection={<IconMail size={16} />}
                disabled={loading}
              />

              <Button
                type="submit"
                fullWidth
                loading={loading}
                leftSection={<IconMail size={16} />}
              >
                Send Reset Link
              </Button>

              <Button
                variant="subtle"
                fullWidth
                leftSection={<IconArrowLeft size={16} />}
                onClick={() => navigate('/login')}
                disabled={loading}
              >
                Back to Login
              </Button>
            </Stack>
          </form>
        </Stack>
      </Paper>
    </Center>
  );
}
